import { css } from '@emotion/core';
import { IThemeVariables } from './themeVariables';

export const getGlobalTypography = (themeVariables: IThemeVariables) => css`
  * {
    font-family: ${themeVariables.type.fontFamily.regular} !important;
    -webkit-font-smoothing: antialiased;
    box-sizing: border-box;
  }

  html {
    font-size: ${themeVariables.type.fontSize.base} !important;
    color: ${themeVariables.colors.type.body};
  }

  body {
    line-height: 1.5;
    color: ${themeVariables.colors.type.body};
    font-weight: ${themeVariables.type.fontWeight[400]} !important;
    font-size: ${themeVariables.type.fontSize.body} !important;
    text-align: left;
  }

  button,
  input {
    line-height: 1;
  }

  a {
    &,
    &:link {
      color: ${themeVariables.colors.action.primary.light};
      cursor: pointer;
      text-decoration: underline;
    }

    &:hover {
      text-decoration: underline;
    }

    &:visited {
      color: ${themeVariables.colors.element.dark};
      text-decoration: underline;
    }
  }

  h1 {
    font-size: ${themeVariables.type.fontSize.h1};
    color: ${themeVariables.colors.element.dark};
    font-weight: ${themeVariables.type.fontWeight[300]};
  }

  h2 {
    font-size: ${themeVariables.type.fontSize.h2};
    color: ${themeVariables.colors.element.dark};
    font-weight: ${themeVariables.type.fontWeight[300]};
  }

  h3 {
    font-size: ${themeVariables.type.fontSize.h3};
    color: ${themeVariables.colors.element.dark};
    font-weight: ${themeVariables.type.fontWeight[300]};
  }

  h4 {
    font-size: ${themeVariables.type.fontSize.h4};
    color: ${themeVariables.colors.element.dark};
    font-weight: ${themeVariables.type.fontWeight[600]};
  }

  h5 {
    font-size: ${themeVariables.type.fontSize.h5};
    color: ${themeVariables.colors.element.dark};
    font-weight: ${themeVariables.type.fontWeight[600]};
  }

  h6 {
    font-size: ${themeVariables.type.fontSize.h6};
    color: ${themeVariables.colors.element.dark};
    font-weight: ${themeVariables.type.fontWeight[300]};
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p {
    max-width: 44em;
  }
`;

const iconSize = (themeVariables: IThemeVariables) => [
  css`
    font-size: ${themeVariables.type.iconSize[0]};
  `,
  css`
    font-size: ${themeVariables.type.iconSize[1]};
  `,
  css`
    font-size: ${themeVariables.type.iconSize[2]};
  `,
  css`
    font-size: ${themeVariables.type.iconSize[3]};
  `,
  css`
    font-size: ${themeVariables.type.iconSize[4]};
  `,
  css`
    font-size: ${themeVariables.type.iconSize[5]};
  `,
  css`
    font-size: ${themeVariables.type.iconSize[6]};
  `
];

const fontColor = (themeVariables: IThemeVariables) => ({
  accent: {
    default: css`
      color: ${themeVariables.colors.accent.default};
    `,
    disabled: css`
      color: ${themeVariables.colors.accent.disabled};
    `,
    light: css`
      color: ${themeVariables.colors.accent.light};
    `
  },

  action: {
    primary: {
      default: css`
        color: ${themeVariables.colors.action.primary.default};
      `,
      light: css`
        color: ${themeVariables.colors.action.primary.light};
      `
    },
    secondary: {
      default: css`
        color: ${themeVariables.colors.action.secondary.default};
      `,
      light: css`
        color: ${themeVariables.colors.action.secondary.light};
      `
    }
  },
  application: {
    background: css`
      color: ${themeVariables.colors.application.background};
    `,
    disabled: css`
      color: ${themeVariables.colors.application.disabled};
    `
  },
  element: {
    dark: css`
      color: ${themeVariables.colors.element.dark};
    `,
    default: css`
      color: ${themeVariables.colors.element.default};
    `,
    light: css`
      color: ${themeVariables.colors.element.light};
    `
  },
  misc: {
    dark: css`
      color: ${themeVariables.colors.misc.dark};
    `,
    light: css`
      color: ${themeVariables.colors.misc.light};
    `,
    medium: css`
      color: ${themeVariables.colors.misc.medium};
    `,
    transparent: css`
      color: ${themeVariables.colors.misc.transparent};
    `,
    white: css`
      color: ${themeVariables.colors.misc.white};
    `
  },
  status: {
    negative: {
      default: css`
        color: ${themeVariables.colors.status.negative.default};
      `,
      light: css`
        color: ${themeVariables.colors.status.negative.light};
      `
    },
    neutral: {
      default: css`
        color: ${themeVariables.colors.status.neutral.default};
      `,
      light: css`
        color: ${themeVariables.colors.status.neutral.light};
      `
    },
    positive: {
      default: css`
        color: ${themeVariables.colors.status.positive.default};
      `,
      light: css`
        color: ${themeVariables.colors.status.positive.light};
      `
    },
    warning: {
      default: css`
        color: ${themeVariables.colors.status.warning.default};
      `,
      light: css`
        color: ${themeVariables.colors.status.warning.light};
      `
    }
  },
  table: {
    alt: css`
      color: ${themeVariables.colors.table.alt};
    `,
    highlight: css`
      color: ${themeVariables.colors.table.highlight};
    `
  }
});

const fontSize = (themeVariables: IThemeVariables) => ({
  body: css`
    font-size: ${themeVariables.type.fontSize.body};
  `,
  h1: css`
    font-size: ${themeVariables.type.fontSize.h1};
  `,
  h2: css`
    font-size: ${themeVariables.type.fontSize.h2};
  `,
  h3: css`
    font-size: ${themeVariables.type.fontSize.h3};
  `,
  h4: css`
    font-size: ${themeVariables.type.fontSize.h4};
  `,
  h5: css`
    font-size: ${themeVariables.type.fontSize.h5};
  `,
  h6: css`
    font-size: ${themeVariables.type.fontSize.h6};
  `,
  small: css`
    font-size: ${themeVariables.type.fontSize.small};
  `,
  tiny: css`
    font-size: ${themeVariables.type.fontSize.tiny};
  `
});

const fontStyle = (themeVariables: IThemeVariables) => ({
  bold: css`
    font-family: ${themeVariables.type.fontFamily.bold};
  `,
  light: css`
    font-family: ${themeVariables.type.fontFamily.light};
  `,
  medium: css`
    font-family: ${themeVariables.type.fontFamily.medium};
  `,
  regular: css`
    font-family: ${themeVariables.type.fontFamily.regular};
  `
});

const fontWeight = (themeVariables: IThemeVariables) => ({
  '100': css`
    font-weight: ${themeVariables.type.fontWeight['100']};
  `,
  '200': css`
    font-weight: ${themeVariables.type.fontWeight['200']};
  `,
  '300': css`
    font-weight: ${themeVariables.type.fontWeight['300']};
  `,
  '400': css`
    font-weight: ${themeVariables.type.fontWeight['400']};
  `,
  '500': css`
    font-weight: ${themeVariables.type.fontWeight['500']};
  `,
  '600': css`
    font-weight: ${themeVariables.type.fontWeight['600']};
  `,
  '700': css`
    font-weight: ${themeVariables.type.fontWeight['700']};
  `,
  '800': css`
    font-weight: ${themeVariables.type.fontWeight['900']};
  `,
  '900': css`
    font-weight: ${themeVariables.type.fontWeight['900']};
  `
});

export interface ITypographyVariables {
  color: object;
  icon: {
    size: object;
  };
  text: {
    size: object;
    style: object;
    weight: object;
  };
}

export const getTypographyVariables = (
  themeVariables: IThemeVariables
): ITypographyVariables => ({
  color: fontColor(themeVariables),
  icon: {
    size: iconSize(themeVariables)
  },
  text: {
    size: fontSize(themeVariables),
    style: fontStyle(themeVariables),
    weight: fontWeight(themeVariables)
  }
});
